'use es6';

import * as DSAssetFamilies from 'customer-data-filters/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { Map as ImmutableMap, Seq } from 'immutable';
import AssociationRecord from 'customer-data-objects/associations/AssociationRecord';
import PortalIdParser from 'PortalIdParser';
import createDSAdapter from './createDSAdapter';
import http from 'hub-http/clients/apiClient';
import { HUBSPOT_DEFINED } from '../../../filterQueryFormat/associations/AssociationCategory';

/**
 * These associations should not be used for filters creation
 * see https://hubspot.slack.com/archives/CA3JB5WLT/p1688142237448589
 */
export const associationsExcludeList = new Set([31, 32, 57, 58, 59, 60, 61, 62]);
export const isExcludedAssociation = (id, category) => {
  if (category === HUBSPOT_DEFINED && associationsExcludeList.has(id)) {
    return true;
  }
  return false;
};
export function makeAssociationDefinitionsAdapter() {
  const cachedAssociationDefinitions = {};
  function parseAssociationDefinitions(associationDefinitions) {
    return Seq.Keyed(associationDefinitions).map(objectAssociations => {
      return ImmutableMap(objectAssociations.reduce((acc, association) => {
        if (isExcludedAssociation(association.id, association.category)) {
          return acc;
        }
        const associationRecord = AssociationRecord({
          cardinality: association.cardinality,
          category: association.category,
          fromObjectType: association.fromObjectType,
          fromObjectTypeId: association.fromObjectTypeId,
          id: association.id,
          inverseId: association.inverseId,
          isPrimary: association.isPrimary || false,
          isInversePrimary: association.isInversePrimary || false,
          maxToObjectIds: association.maxToObjectIds,
          name: association.name,
          toObjectType: association.toObjectType,
          toObjectTypeId: association.toObjectTypeId,
          label: association.label,
          inverseLabel: association.inverseLabel,
          hasAllAssociatedObjects: association.hasAllAssociatedObjects
        });
        acc[association.toObjectTypeId] = acc[association.toObjectTypeId] || ImmutableMap();
        acc[association.toObjectTypeId] = acc[association.toObjectTypeId].set(`${associationRecord.name}__${associationRecord.id}`, associationRecord);
        return acc;
      }, {}));
    }).toMap();
  }
  function isAssetFamilySupported(assetFamily) {
    return assetFamily === DSAssetFamilies.ASSOCIATION_DEFINITION;
  }
  function getFetcherFromProps() {
    return http.get;
  }
  function fetch(httpClient, {
    ids: objectTypeIds
  }) {
    const queryParams = {
      query: {
        portalId: PortalIdParser.get(),
        includeFATypes: true
      }
    };
    const requests = objectTypeIds.map(id => {
      if (cachedAssociationDefinitions[id]) {
        return Promise.resolve(cachedAssociationDefinitions[id]);
      }
      return httpClient(`associations/v1/definitions/${id}`, queryParams).then(response => {
        cachedAssociationDefinitions[id] = response;
        return response;
      });
    });
    return Promise.all(requests).then(associationDefinitions => {
      return objectTypeIds.map((id, index) => [id, associationDefinitions[index]]);
    });
  }
  function updater(associationDefinitions) {
    return parseAssociationDefinitions(associationDefinitions);
  }
  return createDSAdapter({
    fetch,
    getFetcherFromProps,
    isAssetFamilySupported,
    updater
  });
}
const associationDefinitionsAdapter = makeAssociationDefinitionsAdapter();
export default associationDefinitionsAdapter;